import { BackgroundProfileIcon } from "@images/backgroundProfileIcon"
import { ArrowIcon } from "@images/svgIcons/arrowIcon"
import { CheckmarkIcon } from "@images/svgIcons/checkmarkIcon"
import { PencilEditIcon } from "@images/svgIcons/pencilEditIcon"
import { BoxOfferArchivedModal, BoxOfferArchivedModalRef } from "@modules/boxOffer/components/boxOfferArchivedModal"
import { boxOffersInformation } from "@modules/boxOffer/components/boxOfferCardsView"
import {
  BoxOfferFormattedPrice,
  BoxOfferInputCustomLabel,
  BoxOfferRuleCard,
} from "@modules/boxOffer/components/boxOfferFormSection"
import { BoxOfferDetailedEntity, BoxOfferDraftEntity } from "@modules/boxOffer/entities/boxOfferEntity"
import { BoxOfferType, Price } from "@modules/boxOffer/entities/boxOfferTypes"
import { useBoxOffer } from "@modules/boxOffer/hooks/useBoxOffer"
import { useBoxOfferSidebar } from "@modules/boxOffer/hooks/useBoxOfferSidebar"
import { getOfferCommitment, getOfferVisibility } from "@modules/boxOffer/utils/boxOfferUtils"
import { useBoxProgrammations } from "@modules/boxProgrammation/hooks/useBoxProgrammations"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@modules/core/utils/i18n"
import { HUButton } from "@modules/ui/components/huButton"
import { HUTag } from "@modules/ui/components/huTag"
import { HUText } from "@modules/ui/components/huText"
import { capitalize } from "@modules/utils/stringUtils"
import { Archive } from "lucide-react"
import { useCallback, useMemo, useRef } from "react"
import { useTheme } from "styled-components"

type BoxOfferResumeViewProps = {
  formOffer?: BoxOfferDraftEntity // use to create and update offer
  offerId?: string // use to show offer
  rightButtonAction?: () => void
  leftButtonAction?: () => void
  loading?: boolean
}

export const BoxOfferResumeView = ({
  formOffer,
  offerId,
  rightButtonAction,
  leftButtonAction,
  loading,
}: BoxOfferResumeViewProps) => {
  const { navigateToBoxOfferForm } = useBoxOfferSidebar()
  const canEditOffer = useBoxStaffPermission(BoxStaffPermissions.EDIT_OFFER)
  const theme = useTheme()
  const { result: programs } = useBoxProgrammations({ size: 50 })
  const { data: boxOffer } = useBoxOffer(offerId)
  const offer = formOffer ?? boxOffer
  const boxOfferArchivedModal = useRef<BoxOfferArchivedModalRef>(null)

  const offerInformation = useMemo(
    () => boxOffersInformation().find((infos) => infos.type === offer?.type),
    [offer?.type],
  )
  const filteredPrograms = useMemo(
    () => programs.filter((program) => offer?.programs.includes(program.id)),
    [offer?.programs, programs],
  )

  const handleArchiveOffer = async () => {
    if (offer instanceof BoxOfferDetailedEntity) {
      boxOfferArchivedModal.current?.open()
    }
  }

  const handleReactiveOffer = async () => {
    if (offer) {
      offer.archived = false
      navigateToBoxOfferForm(undefined, offer as BoxOfferDetailedEntity)
    }
  }

  const offerCommitment = useMemo(() => {
    return getOfferCommitment(offer)
  }, [offer])

  const visibilitySubtitle = useMemo(() => {
    return getOfferVisibility(offer, theme)
  }, [offer])

  const handleRightAction = useCallback(() => {
    if (offer?.archived)
      return rightButtonAction ?? navigateToBoxOfferForm(undefined, offer as BoxOfferDetailedEntity, true)
    else return rightButtonAction ?? navigateToBoxOfferForm(undefined, offer as BoxOfferDetailedEntity)
  }, [navigateToBoxOfferForm, offer, rightButtonAction])

  return offerInformation && offer ? (
    <>
      <div className="relative" style={{ margin: "-85px -20px" }}>
        {/* HEADER */}
        <div className="flex h-16rem border-round-bottom-2xl mb-5" style={{ backgroundColor: theme.neutralColor50 }}>
          <div className="flex flex-column align-items-center m-auto">
            {offerInformation.icon}
            <HUText fontStyle="HS" className="mt-2 text-center">
              {offer.name}
            </HUText>
            <HUText fontStyle="BM" color={theme.neutralColor700}>
              {capitalize(offerCommitment)}
            </HUText>
            <HUText
              fontStyle="BS"
              color={theme.neutralColor700}
              className="flex my-1 gap-1 justify-content-center align-items-center"
              style={{ whiteSpace: "break-spaces", overflowWrap: "break-word" }}
            >
              {visibilitySubtitle.icon}
              {visibilitySubtitle.text}
            </HUText>

            <BoxOfferFormattedPrice
              price={
                typeof offer.price === "number"
                  ? ({ value: offer.price, currency: offer.currency } as Price)
                  : offer.price
              }
            />
          </div>

          <div
            className={` overflow-hidden ${offer.archived ? "bg-neutralColor-50" : "bg-primary-700"}`}
            style={{ borderBottomRightRadius: "1rem", clipPath: "polygon(32% 0%, 100% 0%, 100% 100%, 0% 100%)" }}
          >
            <BackgroundProfileIcon
              className="bg-no-repeat bg-cover h-full"
              color={offer.archived ? theme.neutralColor300 : theme.primaryColor800}
              style={{ objectFit: "cover" }}
            />
          </div>
        </div>
        {/* CONTENT */}
        <div className="flex flex-column gap-5 p-4 pt-0">
          <HUText
            fontStyle="BM"
            color={theme.neutralColor700}
            style={{ whiteSpace: "break-spaces", overflowWrap: "break-word" }}
          >
            {offer.description}
          </HUText>
          {offer.type !== BoxOfferType.DropIn && (
            <div
              className={`flex justify-content-between gap-5 ${
                ![BoxOfferType.Subscription, BoxOfferType.Millenium].includes(offer.type)
                  ? "text-center"
                  : "flex-column"
              }`}
            >
              {[BoxOfferType.TicketsBook, BoxOfferType.Trial].includes(offer.type) && (
                <div className="w-full">
                  <BoxOfferInputCustomLabel label={word("offer.form.numberOfSessions.title")} />
                  <BoxOfferRuleCard $borderSize={2} $resume={true}>
                    <HUText className="w-full text-center" fontStyle="TS">
                      {word("offer.form.numberOfSessions.included", { count: offer.count ?? 0 })}
                    </HUText>
                  </BoxOfferRuleCard>
                </div>
              )}
              <div className="w-full">
                <BoxOfferInputCustomLabel label={word("box.offer.form.infos.restrictedPeriod.title")} />
                <div
                  style={{
                    display: "grid",
                    gridAutoFlow: [BoxOfferType.Subscription, BoxOfferType.Millenium].includes(offer.type)
                      ? "column"
                      : "row",
                    gap: "0.6rem",
                    gridTemplateColumns: [BoxOfferType.Subscription, BoxOfferType.Millenium].includes(offer.type)
                      ? "repeat(auto-fit, minmax(0, 1fr))"
                      : undefined,
                  }}
                >
                  {offer.rules && offer.rules.length > 0 ? (
                    offer.rules.map((rule, index) => (
                      <BoxOfferRuleCard key={index} $borderSize={2} $resume={true}>
                        <HUText className="w-full text-center" fontStyle="TS">{`${rule.value} ${word(
                          "offer.form.rules.sessions",
                          { count: rule.value },
                        )} ${word(`offer.form.rules.period.per.${rule.period}`)}`}</HUText>
                      </BoxOfferRuleCard>
                    ))
                  ) : (
                    <BoxOfferRuleCard $borderSize={2} $resume={true}>
                      <HUText className="w-full text-center" fontStyle="TS">
                        {word("box.offer.form.infos.rules.unlimited")}
                      </HUText>
                    </BoxOfferRuleCard>
                  )}
                </div>
              </div>
              {BoxOfferType.Trial === offer.type && (
                <div className="w-full">
                  <BoxOfferInputCustomLabel label={word("box.offer.form.infos.renewal.title")} />
                  <BoxOfferRuleCard $borderSize={2}>
                    <HUText className="w-full text-center" fontStyle="TS">
                      {offer.renewable
                        ? word("box.offer.form.infos.renewal.yes")
                        : word("box.offer.form.infos.renewal.no")}
                    </HUText>
                  </BoxOfferRuleCard>
                </div>
              )}
            </div>
          )}
          <div className="mb-5">
            <BoxOfferInputCustomLabel label={word("box.offer.form.infos.activitiesIncluded.title")} />
            <div
              className={`border-2 border-solid border-primary-500 rounded-card px-5 py-4 `}
              style={{ minHeight: "100px" }}
            >
              <HUText fontStyle="BS">{word("box.offer.resume.activitiesIncluded.details")}</HUText>
              <div className="flex flex-wrap gap-2 mt-2">
                {filteredPrograms.map((program, index) => (
                  <HUTag key={index} className="bg-white">
                    <HUText fontStyle="LM" color={theme.mainBlack}>
                      {program.name}
                    </HUText>
                  </HUTag>
                ))}
              </div>
            </div>
          </div>
          {/* PERMISSIONS - Head-coach & Coach & Financial permissions denied */}
          {canEditOffer && (
            <div className={`flex gap-5 mb-4 ${offer.archived && !formOffer ? "flex-row-reverse" : ""}`}>
              {offer.archived && !formOffer ? (
                <HUButton
                  text={word("global.reactivate")}
                  onClick={handleReactiveOffer}
                  type={"Right_Icon"}
                  size="M"
                  colorType="Primary"
                  fullWidth
                  icon={{
                    iconView: <CheckmarkIcon color={theme.mainWhite} width={22} height={22} />,
                  }}
                />
              ) : (
                <HUButton
                  text={leftButtonAction ? word("global.previous") : word("global.archive")}
                  onClick={leftButtonAction ?? handleArchiveOffer}
                  type={formOffer ? "Rounded" : "Right_Icon"}
                  size="M"
                  colorType="Quaternary"
                  fullWidth
                  icon={{
                    iconView: formOffer ? (
                      <ArrowIcon arrowDirection="left" color={theme.mainWhite} width={14} height={14} />
                    ) : (
                      <Archive strokeWidth={1.5} size={15} color={theme.mainWhite} />
                    ),
                  }}
                />
              )}
              <HUButton
                text={formOffer ? word("global.save") : word("global.edit")}
                onClick={handleRightAction}
                type="Right_Icon"
                size="M"
                colorType={offer.archived ? "Quaternary" : "Primary"}
                fullWidth
                disabled={loading}
                submit={formOffer !== undefined}
                icon={
                  formOffer
                    ? undefined
                    : {
                        iconView: <PencilEditIcon color={theme.mainWhite} />,
                      }
                }
              />
            </div>
          )}
        </div>
      </div>
      {offer instanceof BoxOfferDetailedEntity && <BoxOfferArchivedModal ref={boxOfferArchivedModal} offer={offer} />}
    </>
  ) : null
}
